import React from 'react';
import { PipelineStageEditorProps } from '../types';
import Typography from '../../../basic/Typography';
import styles from '../NewsEditor.module.css';

const PipelineStageEditor: React.FC<PipelineStageEditorProps> = ({
  stageName,
  stageKey,
  status,
  isExpanded,
  onToggleExpansion,
  onRerun,
  children,
  customAction
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'processing':
        return '🔄';
      case 'failed':
        return '❌';
      case 'pending':
        return '⏳';
      default:
        return '❓';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'processing':
        return 'var(--color-warning)';
      case 'failed':
        return 'var(--color-error)';
      case 'pending':
        return 'var(--color-text-secondary)';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`${styles['pipeline-stage-editor']} ${isExpanded ? styles['expanded'] : styles['collapsed']}`}>
      {/* Stage Header */}
      <div
        className={styles['pipeline-stage-header']}
        onClick={onToggleExpansion}
      >
        <div className={styles['stage-header-left']}>
          <span className={`${styles['stage-expand-icon']} ${isExpanded ? styles['expanded'] : ''}`}>
            ▼
          </span>
                    <Typography variant="h4" className={styles['stage-name']}>{stageName}</Typography>
          <span
            className={styles['stage-status-icon']}
            style={{ color: getStatusColor(status.status) }}
          >
            {getStatusIcon(status.status)}
          </span>
          <Typography variant="caption" color="secondary" className={styles['stage-status-text']}>{status.status}</Typography>
        </div>

        <div className={styles['stage-header-right']}>
          {/* Custom action for specific stages */}
          {customAction}

          {/* Hide generic rerun button for stages that have dedicated rerun buttons or don't need rerun */}
          {stageName !== 'News Aggregation' && stageName !== 'Script Generation' && stageName !== 'Rendering Engine' && stageName !== 'Composition' && stageName !== 'Create New Job' && (
            <button
              className={styles['stage-rerun-button']}
              onClick={(e) => {
                e.stopPropagation();
                onRerun();
              }}
              disabled={status.status === 'processing'}
              title={`Rerun ${stageName} stage`}
            >
              {status.status === 'processing' ? '🔄' : '🔄'}
              Rerun
            </button>
          )}
        </div>
      </div>

      {/* Stage Content */}
      <div className={`${styles['pipeline-stage-content']} ${isExpanded ? styles['expanded'] : ''}`}>
        {isExpanded && (
          <>
            {/* Stage Status Info */}
            <div className={styles['stage-status-info']}>
                          <div className={styles['status-details']}>
              <Typography variant="caption" color="secondary">Last Updated: {formatDate(status.last_updated)}</Typography>
              {status.progress !== undefined && (
                <Typography variant="caption" color="secondary">Progress: {status.progress}%</Typography>
              )}
              {status.error && (
                <Typography variant="caption" color="secondary" className={styles['error-message']}>Error: {status.error}</Typography>
              )}
            </div>

              {/* Progress Bar */}
              {status.progress !== undefined && (
                <div className={styles['progress-bar']}>
                  <div
                    className={styles['progress-fill']}
                    style={{
                      width: `${status.progress}%`,
                      backgroundColor: getStatusColor(status.status)
                    }}
                  ></div>
                </div>
              )}
            </div>

            {/* Stage Content */}
            <div className={styles['stage-content']}>
              {children}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PipelineStageEditor;
